import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { graphql, useStaticQuery } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import pt from 'prop-types';
import React, { useState } from 'react';
import { useHeadlineContext } from '../../../context/HeadlineProvider/HeadlineProvider';
import { combine } from '../../../helpers/styles';
import Link from '../link';
import Text from '../text';
import * as styles from './styles.module.scss';

const RichText = ({
  content,
  paragraphAs,
  paragraphType,
  paragraphVariant,
  className,
  textClassName,
}) => {
  const [, isHeadlineSet] = useHeadlineContext(true);
  const [headline] = useState(!isHeadlineSet);
  const useContentfulLink = (id) => {
    const { links } = useStaticQuery(
      graphql`
        query CONTENTFUL_LINK_QUERY {
          links: allContentfulLink {
            edges {
              node {
                contentful_id
                externalLink
                text: label
                linkToPage {
                  ... on ContentfulPressRelease {
                    slug
                    internal {
                      type
                    }
                  }
                  ... on ContentfulArticle {
                    slug
                    internal {
                      type
                    }
                  }
                  ... on ContentfulPage {
                    slug
                    internal {
                      type
                    }
                  }
                }
                linkToDynamicPage
                dynamicDetailPageId
                dynamicDetailPageName
                queryParameter
                icon
                type
                transparent: isTransparent
              }
            }
          }
        }
      `
    );
    const link = links.edges.find(({ node }) => node.contentful_id === id);
    return link;
  };

  const options = {
    renderNode: {
      [BLOCKS.TABLE_HEADER_CELL]: function renderTableHeaderCell(
        _node,
        children
      ) {
        const emptyHeader = !children?.[0]?.props?.children?.[0][1];
        return (
          <th className={!emptyHeader && styles.spacing}>
            <Text as="h3" type="h3" className={styles.heading}>
              {children}
            </Text>
          </th>
        );
      },
      [BLOCKS.PARAGRAPH]: function renderCopyText(_node, children) {
        return (
          <Text
            as={paragraphAs}
            type={paragraphType}
            variant={paragraphVariant}
            className={textClassName}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_1]: function renderHeading1(_node, children) {
        return (
          <Text as="h2" type="h1" className={combine(styles.hl, textClassName)}>
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_2]: function renderHeading2(_node, children) {
        return (
          <Text
            as={headline ? 'h2' : 'h3'}
            type="h2"
            className={combine(styles.hl, styles.h2, textClassName)}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HEADING_3]: function renderHeading3(_node, children) {
        return (
          <Text
            as={headline ? 'h3' : 'h4'}
            type="h3"
            variant="semibold"
            className={combine(styles.hl, styles.h3, textClassName)}
          >
            {children}
          </Text>
        );
      },
      [BLOCKS.HR]: function renderEmbeddedHR(_node, children) {
        return children;
      },
      [BLOCKS.OL_LIST]: function renderEmbeddedUL(_node, children) {
        return <ol className={styles.ol}>{children}</ol>;
      },
      [BLOCKS.UL_LIST]: function renderEmbeddedUL(_node, children) {
        return <ul className={styles.ul}>{children}</ul>;
      },
      [BLOCKS.LIST_ITEM]: function renderEmbeddedLI(_node, children) {
        return <li>{children}</li>;
      },
      [INLINES.HYPERLINK]: function renderHyperlink(node, children) {
        return (
          <Link
            link={{ externalLink: node.data.uri?.replace(/^mailto\:/, '') }}
            mailto={/^mailto\:/.test(node.data.uri)}
          >
            <span className={styles.link}>{children}</span>
          </Link>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: function renderEntryHyperlink(node, children) {
        const link = useContentfulLink(node.data?.target?.sys.id);
        if (!link) return null;
        return (
          <Link link={link.node}>
            <span className={styles.link}>{children}</span>
          </Link>
        );
      },
    },
    renderText: (text) => {
      return text.split('\n').reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
  };
  return <div className={className}>{renderRichText(content, options)}</div>;
};

export const richTextPropTypes = {
  raw: pt.string,
};

RichText.propTypes = {
  content: pt.shape(richTextPropTypes).isRequired,
  paragraphType: pt.string,
  paragraphVariant: pt.string,
  paragraphAs: pt.string,
  className: pt.string,
  textClassName: pt.string,
};

RichText.defaultProps = {
  paragraphType: 'p1',
  paragraphVariant: '',
  paragraphAs: 'p',
  className: '',
  textClassName: '',
};

export default RichText;
